import { Box, Checkbox, Stack, Typography } from '@mui/material'
import Panel from './Panel'
import { FS, SP, TOUCH_TARGET_COARSE, TOUCH_TARGET_COARSE_QUADRAT } from '../../theme/tokens'

const GROBER_ZEIGER = '@media (pointer: coarse)'

/**
 * KARTE_TITEL_BASIS_SX — der gemeinsame Boden des Kartentitels (R16-W4,
 * 2026-10-21).
 *
 * 🔴 **Anlass:** drei Seiten trugen denselben 44-px-Boden als eigene Kopie
 * (`POOverview.KARTE_TITEL_SX`, `VendorsOverview.KARTE_TITEL_SX`, und
 * `ProductsTable.KARTE_TITEL_SX` mit zusätzlicher Klemme). Ändert eine Seite
 * ihn, driften die anderen still.
 *
 * 🔴 **`ProductsTable` ist ABSICHTLICH nicht umgehängt: sein Boden hängt an der
 * Oberkante und trägt 700; die 44 px sind gemeinsam, die Fläche nicht.** Hier
 * stand bis zum 2026-10-21 „Der Boden ist wörtlich identisch" — das galt für
 * die zwei umgehängten Seiten (bis auf `whiteSpace`/`overflowWrap`), nicht für
 * `ProductsTable.KARTE_TITEL_SX`: dort steht `fontWeight: 700` und der
 * `::after`-Anker ist `top: -8px` (Oberkante, ohne `transform`), nicht
 * `top: '50%'` + `translateY(-50%)`. Wer diese Basis dort spreadet, verschiebt
 * den Boden um 22 px und reißt die Trefferfläche (R15-Runde-3 C3).
 *
 * **Hier steht nur, was ALLEN gemeinsam ist:** die Fläche (`display: block` +
 * `position: relative`, Träger des `::after`) und der 44-px-Boden auf grobem
 * Zeiger. Die Zeilenregel ist ABSICHTLICH nicht hier: sie unterscheidet sich
 * je Seite und ist eine Inhaltsentscheidung — Kennungen (`nowrap`) gegen Namen
 * (`overflowWrap: 'anywhere'`). Jede Seite spreadet diese Basis und setzt ihre
 * Abweichung danach.
 *
 * 🔴 Der Träger muss ein Element sein, das `sx` annimmt (MUI `Link`), nicht ein
 * Router-`Link` mit `style`: ein Inline-Stil kann keine Media-Query aufnehmen,
 * der Boden fiele still weg.
 */
export const KARTE_TITEL_BASIS_SX = {
  fontWeight: 600,
  display: 'block',
  position: 'relative',
  [GROBER_ZEIGER]: {
    '&::after': {
      content: '""',
      position: 'absolute',
      left: 0,
      right: 0,
      top: '50%',
      height: TOUCH_TARGET_COARSE[GROBER_ZEIGER].minHeight,
      transform: 'translateY(-50%)',
    },
  },
}

/**
 * MobileRecordCard — die eine Kartenform für eine Tabellenzeile auf dem Telefon.
 *
 * @description
 *   Eine Tabelle wird bei 390 px zur waagerechten Rollfläche: die letzte Spalte
 *   steht außerhalb des Bildes, und was dort steht, sieht niemand ohne zu
 *   wischen. Auf `/rma/faelligkeit` ist die letzte Spalte die **Frist** — die
 *   Angabe, für die die Seite überhaupt existiert. Diese Karte ist die Zeile
 *   als Fläche: dieselben Felder, untereinander statt nebeneinander, damit die
 *   Frist ohne Rollen sichtbar ist.
 *
 *   🔴 Sie ist die **minimale gemeinsame Form**, nicht die Vereinheitlichung der
 *   bestehenden Karten. `InboxCard` (RMA) und `InboxCard` (PO) tragen
 *   **verschiedene Aktionssätze** (RMA: Lesen/Verwerfen · PO: Ignorieren/Neu
 *   verarbeiten/Verknüpfen) — eine von beiden zur allgemeinen zu erklären hätte
 *   der anderen ihre Handlungen aufgezwungen. Diese Karte trägt deshalb **nur**
 *   die Anatomie und **keinen** Aktionssatz: sie weiß nicht, was ein Fall ist.
 *
 *   🔴 Der **Datensatz steht vorn** — die Kennung ist der Weg hinein und
 *   gehört in die erste Zeile, wie die erste Spalte der Tabelle, die die Karte
 *   ersetzt. Die übrigen Felder folgen der Anatomie der Karte — `actions`
 *   rechts im Kopf neben dem Titel, darunter `meta`, dann `chips` —, nicht
 *   einer neuen Lesart. Wer die Karte auf einer zweiten
 *   Seite einsetzt, prüft zuerst, ob seine erste Spalte auch dort die Kennung
 *   ist; wenn nicht, ist das ein Befund über die zweite Seite, kein Grund, die
 *   Karte umzubauen.
 *
 *   🔴 **Der Auslöser ist die Breite, nicht die Eingabeart.** Die Weiche ist
 *   eine Viewport-Abfrage; eine zweite Grenze am Zeigertyp formte dieselbe
 *   Seite in zwei Breiten verschieden, und der Nutzer sähe die Form wechseln,
 *   ohne dass sich die Breite sichtbar ändert. Die Begründung des ersten
 *   Verbrauchers steht in `rma/RMAFaelligkeit.jsx:637-642`.
 *
 *   🔴 **Sechs neue Props, alle opt-in — ohne sie rendert die Karte Zeichen für
 *   Zeichen wie vorher** (UI2 Teil 10, G0.8 / BS12, 2026-10-12; `slotOrder`
 *   seit G1b, 2026-10-14; `expanded` seit G4-V, 2026-10-16; Konzept
 *   `tabellen-filter-suche.md` §11.2/§12).
 *   `id` ist der Slot der **Kennung**
 *   („RMA-2099-0001"), direkt unter der Titelzeile und **vor** `meta` — dort
 *   steht sie in §11.2 als ② unmittelbar unter dem Kopf, wie die erste Spalte
 *   der Tabelle, die die Karte ersetzt. Sie wird **nie gekürzt**: `nowrap` und
 *   **kein** `overflow: hidden` / `text-overflow: ellipsis` — der Kasten wächst
 *   mit dem Wert (§11.2 N11.5, Probe P15). `variant="row"` ist die
 *   **reduzierte Zeile** (N11.7: Bericht, Admin, eingebettete Listen): Zeile 1
 *   Titel und rechts Status (`chips`) und Aktion, Zeile 2 Kennung und `meta`,
 *   **Trennlinie statt Rahmen** — sie rendert deshalb **kein** `Panel`; auf
 *   grobem Zeiger trägt sie den 44-px-Boden (`TOUCH_TARGET_COARSE`, N11.19 —
 *   die Zeile ist bedienbar, der Titel ist der Weg hinein).
 *   `fieldTestIds` stempelt `data-testid` an die einzelnen Feldplätze (P15
 *   greift über diese Marken zu); `selectable` hängt im Auswahlmodus (§11.6,
 *   G0.9) eine **echte** Checkbox mit Namen vor den Kopf — kein `<Box>` mit
 *   `role="checkbox"` (WCAG 2.1.1) und auf grobem Zeiger 44 px (Regel 22 §8).
 *   🔴 **`selectable.label` ist Pflicht und die Komponente wirft ohne ihn**
 *   (Hausform `EmptyState.reason`, `ResultCount.noun`, `RefreshButton.ariaLabel`):
 *   ein Rückfallname hiesse in einer Liste mit fünfzig Karten fünfzigmal
 *   dasselbe — genau das, was BS18 für die Zeilenaktion verbietet.
 *
 *   🔴 **Die Vorgaben sind das alte Verhalten.** `variant` fehlt → `'card'`
 *   (die heutige Form); `id`, `fieldTestIds`, `selectable` fehlen → die Slots
 *   entstehen nicht; `slotOrder` fehlt → `'meta-first'`, die heutige Folge.
 *   Die zwei Bestandsaufrufer (`rma/RMAFaelligkeit.jsx:471`,
 *   `design/muster/ListenMuster.jsx:1025`) sind unverändert, und
 *   `MobileRecordCard.test.jsx` hält den Grundzustand als erste Zusicherung
 *   fest (Regel 17 §1: „ohne neue Props wie vorher" fällt unter „verändert"
 *   und „unverändert" nur dann unterschiedlich aus, wenn der Grundzustand
 *   geprüft wird).
 *
 *   🔴 **`slotOrder` — die Reihenfolge von `meta` und `chips`** (UI2 Teil 10,
 *   G1b, 2026-10-14). N11.2 (`tabellen-filter-suche.md:731`) verlangt
 *   **Titel → Kennung → Status → Kerndaten**; dieser Baustein rendert in der
 *   `card`-Variante **Titel → Kennung → Kerndaten → Status**. Die globale
 *   Drehung ist **verworfen**, und zwar gemessen: `frontend/tests/design/ui2-listen-karten.spec.ts`
 *   L.1g misst die Oberkante der Fristzeile gegen die Kartenoberkante (Grenze
 *   `FRIST_OBEN_MAX_H` 100 px + 0,5, heute 81 px, Marge 19,5 px). Die Frist
 *   sitzt in `meta` (`rma/RMAFaelligkeit.jsx:540`), die Richtung in `chips`
 *   (`:596`, ein `Pill` ≈ 28 px) — die Drehung schöbe die Frist auf ≈ 109 px
 *   und machte L.1g **rot**. Sie änderte außerdem die Musterroute, die
 *   denselben Baustein benutzt (`ListenMuster.jsx:1025`).
 *   `slotOrder="chips-first"` dreht deshalb **je Aufrufer** und lässt die
 *   Vorgabe stehen. Erster und bislang einziger Verbraucher: `/products`
 *   (`ProductsTable.jsx`). Die weiteren Karten der Serie (G2b/G3b/G4b/G5b)
 *   setzen es ebenfalls opt-in.
 *
 *   ⚠️ **`slotOrder` wirkt NUR in der `card`-Variante.** Die reduzierte Zeile
 *   (`variant="row"`) trägt `chips` schon nach N11.7 in Zeile 1 und `meta` in
 *   Zeile 2 — dort gibt es nichts zu drehen, und ein Prop, das dort still
 *   nichts täte, wäre eine Zusicherung ohne Gegenstand.
 *
 *   🔴 **`expanded` — der Platz für den aufgeklappten Teil** (UI2 Teil 10,
 *   G4-V, 2026-10-16). Die zwei Inbox-Karten klappen **in der Karte** auf
 *   (`Collapse` in `RMAInbox.jsx` und `POInbox.jsx`) — dieser Baustein hatte
 *   dafür **keinen** Platz, und genau das war der Vorbau (§2.6 der Karte A4:
 *   die gemeinsame Inbox-Variante **abgeleitet**, nicht eine der beiden
 *   bestehenden zur allgemeinen erklärt). `expanded` ist ein `ReactNode` und
 *   rendert **unter** `chips`, in derselben Spalte, mit `mt: SP.chip` — also
 *   unter dem letzten Block, den `slotOrder` setzt, in **beiden**
 *   Reihenfolgen. Ohne die Prop entsteht **kein** Knoten: die Karte ist
 *   Zeichen für Zeichen dieselbe (K.7a vergleicht gegen `SIGNATUR_VORGABE`).
 *   🔴 Ein **leeres Array** zählt wie „nicht gesetzt" (R15 W3, Runde 1):
 *   `expanded={liste.map(…)}` schickt bei leerer Liste `[]` — truthy —, und
 *   ohne die Array-Wache entstünde ein leerer Kasten mit `mt: SP.chip`.
 *   Die Probe K.7f hält es fest.
 *
 *   ⚠️ **`expanded` wirkt NUR in der `card`-Variante**, wie `slotOrder` — und
 *   aus demselben Grund. `variant="row"` ist die **reduzierte Zeile** (N11.7)
 *   mit **zwei** Zeilen, „nichts geht verloren, nichts kommt hinzu"; ein
 *   dritter Block machte aus ihr eine dritte Form. Die Zeile ist für Bericht,
 *   Admin und eingebettete Listen gebaut, nicht für einen aufgeklappten
 *   Zustand. Die Probe K.7d hält das fest.
 *
 *   🔴 **Die Karte bringt die Aufklapp-Mechanik NICHT mit.** Kein `Collapse`,
 *   kein Schalter, kein `aria-expanded`: was hereinkommt, ist fertig gerendert
 *   oder gar nicht. Der Zustand gehört dem Aufrufer — dieselbe Trennung wie
 *   bei der Weiche („Der Auslöser ist die Breite") und beim Aktionssatz. Ein
 *   Baustein, der ihn selbst hielte, hätte eine **zweite** Quelle für
 *   `expanded` neben der des Aufrufers, und die Karte weiss nicht, was ein
 *   Fall ist.
 *
 *   🔴 **Und im GESCHLOSSENEN Zustand gibt der Aufrufer `expanded` gar nicht
 *   mit.** Der eingeklappte Bereich darf **keinen fokussierbaren und keinen
 *   angesagten Inhalt** behalten — `unmountOnExit` oder gleichwertig. Ob MUIs
 *   `Collapse` ohne `unmountOnExit` das über sein eigenes `visibility: hidden`
 *   schon deckt, ist **nicht gemessen** (dem Prüfbaum der Runde 1 fehlte
 *   `node_modules`) und ausdrücklich an **G4b** übergeben (G4-Plan, Abschnitt
 *   „Übergabe an G4b").
 *
 *   🔴 **Ein Bedienelement in einem Baum mit `gesteFuer` benutzt ENTWEDER den
 *   Gesten-`onClick` ODER stoppt `onPointerDown`.** Der Langdruck der
 *   Kartenliste baut EINE Instanz (`auswahl.gesteFuer(key, onTap)`) und legt
 *   sie auf die Fläche; ein zweiter Klickpfad an einem Kind, das diese Instanz
 *   NICHT benutzt, markiert beim Langdruck und wählt beim Loslassen sofort
 *   wieder ab: der `pointerup` setzt `verschluckeKlickRef`, der eigene Handler
 *   liest die Marke nicht, und sein `click` schaltet zurück. Genau so ist es
 *   auf `/rma` passiert (G5b-Nachbesserung 4, R16 B1, 2026-10-18) — der Titel
 *   trug `handleCardClick`, die Fläche die Geste. Die zwei zulässigen Formen:
 *   `geste.onClick` benutzen — der Titel (`RMACasesDashboardBody.jsx:2641`
 *   Instanz, `:2654` ihr `onClick`) und die Fläche (`:2957`, `{...geste}`) —
 *   oder `onPointerDown`-`stopPropagation` am Träger (Kästchen, ⋮); ein
 *   dritter Weg existiert nicht; wer eine vierte Karte baut, kopiert den Fehler.
 *
 * @useWhen
 *   - Eine Tabelle mit mehr als drei Spalten soll auf schmalen Viewports als
 *     Kartenliste erscheinen
 *   - 🔴 **Die Weiche ist die Entscheidung des AUFRUFERS** — dieser Baustein
 *     bringt keine mit: `secondLife/OpenCases.jsx` schaltet mit
 *     `useMediaQuery(theme.breakpoints.down('md'), { noSsr: true })`, weil bei
 *     900 … 1199 sonst eine 922-px-Tabelle in einen 694-px-Kasten fiele
 *     (Owner-Befund A, gemessen bei 768)
 *   - 🔴 **Eine Kartenliste auf BEIDEN Breiten**, wenn es gar keine Tabelle gibt
 *     (A5 `/scrapers/redirect-reviews`, G6a 2026-10-20): dieselbe Anatomie, nur
 *     ohne Weiche — Titel, Kennung, `chips` (Status), `meta` (Kerndaten),
 *     `expanded` (die immer offenen Zusatzfelder) und `actions` (EIN sichtbarer
 *     Knopf + ⋮). Die `expanded`-Mechanik bringt auch hier der Aufrufer mit; A5
 *     übergibt einen Knoten ohne Schalter, weil es dort nichts zuzuklappen gibt
 *   - Die Felder sind eine feste, kurze Liste — nicht eine beliebige Menge
 *   - Die Zeile trägt **keine** Wischgeste (dafür ist sie nicht gebaut; eine
 *     Wischzeile ist `useSwipeRow` plus eine eigene Fläche)
 *   - Die Liste führt einen **Status** als `chips` und **Kerndaten** als `meta`,
 *     und die Karte soll N11.2 folgen (Status vor Kerndaten) → `slotOrder="chips-first"`
 *   - Die Zeile klappt **in der Karte** auf (Inbox-Karten, `Collapse`) → der
 *     aufgeklappte Teil ist `expanded`; die Karte bleibt **eine** Form, statt
 *     eine zweite daneben zu stellen. Gilt für die **Karten** der mobilen
 *     Inbox (G4b) — **nicht** für die `InboxRow`-Zeilen der Desktop-Liste
 *   - 🔴 **Ein Bedienelement ist der TITEL**, nicht die Karte: der Aufrufer
 *     gibt ihn als echten Knopf (`<button>`/`<Link>`) mit `aria-expanded` aus,
 *     wenn die Karte aufklappt. Ein Zeiger-Tipp auf die **freie Fläche** darf
 *     dieselbe Handlung auslösen (Komfort) — er ist **nicht** im A11y-Baum und
 *     darf `selectable` und `actions` nicht verschlucken (der Aufrufer nimmt
 *     beide per `stopPropagation` aus). Hausform seit G4b, Orchestrator-
 *     Entscheid A vom 2026-10-18
 *
 * @dontUseFor
 *   - Die **`InboxRow`-Zeilen der Desktop-Liste** — die tragen ihre eigenen
 *     Aktionssätze (`InboxCard` in `POInbox.jsx` / `RMAInbox.jsx`); sie zu
 *     ersetzen ist die Vereinheitlichung aus M5-9 und **nicht** Teil dieses
 *     Bausteins. ⚠️ Die **mobilen Inbox-KARTEN** mit aufklappbarem Teil sind
 *     dagegen mit **G4b** die erste Variante **dieses** Bausteins — die
 *     Abgrenzung fällt dort
 *   - Eine Karte mit Bild, Diagramm oder freiem Innenleben — dafür `Panel`
 *   - 🔴 **`role="button"`/`tabIndex` am Wurzelknoten.** Die Rolle macht die
 *     Nachfahren präsentational: eine Checkbox, ein Hauptknopf und ein ⋮ darin
 *     verschwinden aus dem Accessibility-Baum (R16 B1, 2026-10-18). Das
 *     Bedienelement ist der **Titel** — die Fläche bleibt Zeiger-Komfort
 *
 * @related
 *   - `Panel` — die Fläche darunter; diese Karte setzt nur Anatomie darauf
 *   - `Pill` — die Chipform für `leading`, `chips` und `actions`
 *   - `TableViewport` — die Desktop-Hälfte derselben Weiche
 *   - `InboxCard` (POInbox/RMAInbox) — die reicheren Karten **dieses**
 *     Bausteins; ihr aufgeklappter Teil ist der erste Verbraucher von
 *     `expanded` (G4b)
 *
 * @reuseHints
 *   - `/rma/faelligkeit` ist der erste Verbraucher (`variant="card"`, Vorgabe);
 *     `/products` ist seit G1b der zweite und der erste mit
 *     `slotOrder="chips-first"`; die Musterroute (`design/muster`) fährt die
 *     Vorgabe. Die übrigen Listen (Lieferanten, Scraper, Fulfillment) folgen
 *     erst, wenn die Form sich dort bewährt hat — ein Umbau von fünf Seiten auf
 *     eine Vermutung wäre teurer als die Doppelung, die er auflöst
 *   - Der `chips`-Platz trägt beim ersten Verbraucher die **Richtung**
 *     („Customer" / „Vendor" / „Warehouse") — ein Merkmal der Zeile, kein Wert.
 *     Er gehört der Zeile, die **mehrere** gleichrangige Merkmale trägt (z. B.
 *     die Inbox-Karten mit „unread" + „Auto-handled"); er wird nicht gefüllt,
 *     nur damit er voll ist
 *
 * @props
 *   leading — links im Kopf, vor dem Titel: ein Merkmal der Zeile (Chip, Symbol).
 *             Beim ersten Verbraucher **unbesetzt** — die Richtung steht in
 *             `chips`, damit die Kennung vorn bleibt
 *   title   — die Hauptzeile, **Pflicht**: der Datensatz und sein Weg hinein
 *   id      — der Slot der **Kennung** („RMA-2099-0001"): direkt unter der
 *             Titelzeile, **vor** `meta`, `monospace`, **nie gekürzt** (G0.8)
 *   meta    — der Block unter dem Titel: die übrigen Felder, vom Aufrufer gesetzt
 *   chips   — eine Zeile gleichrangiger Merkmale (`Pill`s), unter `meta`
 *   actions — rechts im Kopf: die Handlung oder ihr Abzeichen (`Pill`, Knopf)
 *   variant — `'card'` (Vorgabe: die heutige Form) | `'row'` (reduzierte
 *             Zeile, N11.7 — Trennlinie statt Rahmen, **kein** `Panel`;
 *             auf grobem Zeiger 44 px Boden, N11.19)
 *   slotOrder — `'meta-first'` (Vorgabe: `meta` vor `chips`, die heutige Folge)
 *             | `'chips-first'` (N11.2: Titel → Kennung → Status → Kerndaten).
 *             Wirkt **nur** in der `card`-Variante (die `row`-Form trägt
 *             `chips` ohnehin in Zeile 1, N11.7)
 *   expanded — der **aufgeklappte Teil** der Karte (`ReactNode`), **unter**
 *             `chips` und **unter** dem letzten Block, den `slotOrder` setzt;
 *             ohne die Prop entsteht **kein** Knoten, und ein **leeres Array**
 *             zählt wie „nicht gesetzt". Wirkt **nur** in der
 *             `card`-Variante (die `row`-Form ist die zweite, reduzierte Form,
 *             N11.7). 🔴 Die Aufklapp-Mechanik (`Collapse`, `aria-expanded`)
 *             bringt der **Aufrufer** mit — die Karte hält keinen Zustand
 *   fieldTestIds — `{ title?, id?, meta?, chips?, actions?, leading?, select? }`
 *             → `data-testid` am jeweiligen Feldplatz (die Prüffläche P15);
 *             ohne die Prop entsteht **kein** Attribut
 *   selectable — `false` (Vorgabe) | `{ checked, onChange, label, disabled? }`:
 *             eine **echte** Checkbox mit Namen, links im Kopf (§11.6).
 *             🔴 `label` ist **Pflicht** (Zeilenbezug) — ohne es wirft die
 *             Komponente, statt einen Rückfallnamen zu setzen. Der Klick auf
 *             das Kästchen wird an seinem Träger gestoppt (`onClick` **und**
 *             `onPointerDown`), damit der Gesten-`onClick` der Karte im
 *             Auswahlmodus nicht ein ZWEITES Mal umschaltet (R16 B1).
 *   sx      — zuletzt angewandt, der Aufrufer gewinnt
 */
export default function MobileRecordCard({
  leading,
  title,
  id,
  meta,
  chips,
  actions,
  variant = 'card',
  slotOrder = 'meta-first',
  expanded,
  fieldTestIds,
  selectable,
  sx,
  ...rest
}) {
  // 🔴 `data-testid` je Feldplatz — NUR wenn der Aufrufer ihn setzt. Ohne
  // `fieldTestIds` entsteht kein Attribut; das ist die additive Zusicherung.
  const tid = (feld) => {
    const wert = fieldTestIds && fieldTestIds[feld]
    return wert ? { 'data-testid': wert } : null
  }

  // 🔴 Ein Auswahlkasten ohne Namen ist unerreichbar, und ein RUECKFALLname ist
  // hier keine Loesung: in einer Liste mit fuenfzig Karten hiesse er fuenfzigmal
  // dasselbe — genau das, was BS18 fuer die Zeilenaktion verbietet
  // (`pattern-library.md` §Zeilenaktion). Hausform wie `EmptyState.reason`,
  // `ResultCount.noun` und `RefreshButton.ariaLabel`: der Name ist PFLICHT und
  // die Komponente wirft ohne ihn, statt still einen Platzhalter zu setzen.
  if (selectable && !selectable.label) {
    throw new Error(
      'MobileRecordCard: `selectable` braucht ein `label` — den zugänglichen Namen MIT Zeilenbezug ' +
        '(z. B. "Select RMA-2099-0001"). Ohne ihn hiesse die Checkbox in jeder Zeile gleich.'
    )
  }

  // 🔴 Eine ECHTE Checkbox mit Namen — kein `<Box role="checkbox">` (WCAG
  // 2.1.1). Der Name kommt über `slotProps.input` (Hausform), weil MUI ihn
  // sonst nirgends herbekommt; `TOUCH_TARGET_COARSE_QUADRAT` gibt auf grobem
  // Zeiger 44 x 44 (Regel 22 §8) — ohne es bliebe der Kasten bei 28 px und
  // wäre ein Oval.
  const auswahl = selectable ? (
    // 🔴 **Der Klick auf das Kästchen darf NUR die Checkbox erreichen**
    // (G5b-Nachbesserung 2, R16 B1). Der Kartenkasten trägt den Gesten-`onClick`
    // des Aufrufers; im Auswahlmodus schaltet der um. Ohne diese Hülle feuert
    // ein Tipp auf das Kästchen ZWEIMAL `toggle` — einmal über MUI `onChange`,
    // einmal über den hochblubbernden Klick —, und das Ergebnis ist netto null.
    // Die Tabelle löst dasselbe an ihrer Checkbox-Zelle
    // (`RMACasesDashboardBody.jsx`, `onClick` an der `padding="checkbox"`-Zelle);
    // `onPointerDown` ist die Hausform gegen den Langdruck-Start der Geste
    // (`pattern-library.md:478`).
    <Box
      sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      {...tid('select')}
    >
      <Checkbox
        size="small"
        checked={Boolean(selectable.checked)}
        onChange={selectable.onChange}
        disabled={Boolean(selectable.disabled)}
        sx={{ p: 0, ...TOUCH_TARGET_COARSE_QUADRAT }}
        slotProps={{ input: { 'aria-label': selectable.label } }}
      />
    </Box>
  ) : null

  // 🔴 Die Kennung wird NIE gekürzt: `nowrap` und **kein**
  // `overflow: hidden` / `text-overflow: ellipsis`. Ein `ellipsis` hier wäre
  // genau die Mutation, die die Probe K.2 rot macht (§11.2 N11.5).
  const kennung = id ? (
    <Typography
      variant="caption"
      component="div"
      {...tid('id')}
      sx={{
        fontFamily: 'monospace',
        fontSize: FS.sm,
        color: 'text.secondary',
        whiteSpace: 'nowrap',
      }}
    >
      {id}
    </Typography>
  ) : null

  // 🔴 N11.7 — die reduzierte Zeile: **Trennlinie statt Rahmen** (deshalb kein
  // `Panel`), Zeile 1 Titel + rechts Status (`chips`) und Aktion, Zeile 2
  // Kennung + `meta`. Nichts geht verloren, nichts kommt hinzu (§11.2 N11.3).
  //
  // 🔴 N11.19 — die Zeile ist bedienbar (der Titel ist der Weg hinein), also
  // traegt SIE auf grobem Zeiger den 44-px-Boden. N11.7 nennt die Zeile fuer
  // Bericht/Admin/eingebettet und sagt nichts ueber den Zeigertyp; ein Tablet
  // faehrt dieselbe Form mit dem Finger. Der Boden sitzt an der ZEILE, damit
  // die Flaeche ihn traegt, die der Finger trifft — der Kartenzweig bleibt
  // unveraendert (dort traegt ihn der Aufrufer, `RMAFaelligkeit.jsx:477-512`).
  if (variant === 'row') {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: SP.chip,
          px: SP.tight,
          py: SP.tight,
          borderBottom: '1px solid',
          borderColor: 'divider',
          ...TOUCH_TARGET_COARSE,
          ...(sx || {}),
        }}
        {...rest}
      >
        <Stack direction="row" spacing={SP.tight} alignItems="center">
          {auswahl}
          {leading ? (
            <Box sx={{ flexShrink: 0, display: 'flex' }} {...tid('leading')}>
              {leading}
            </Box>
          ) : null}
          <Box sx={{ minWidth: 0, flex: 1 }} {...tid('title')}>
            {title}
          </Box>
          {chips ? (
            <Stack
              direction="row"
              spacing={SP.chip}
              useFlexGap
              flexWrap="wrap"
              sx={{ flexShrink: 0 }}
              {...tid('chips')}
            >
              {chips}
            </Stack>
          ) : null}
          {actions ? (
            <Box sx={{ flexShrink: 0 }} {...tid('actions')}>
              {actions}
            </Box>
          ) : null}
        </Stack>
        {kennung || meta ? (
          <Stack direction="row" spacing={SP.chip} alignItems="center" useFlexGap flexWrap="wrap">
            {kennung}
            {meta ? <Box {...tid('meta')}>{meta}</Box> : null}
          </Stack>
        ) : null}
      </Box>
    )
  }

  // 🔴 Die zwei Blöcke stehen als VARIABLEN, damit `slotOrder` sie tauschen
  // kann, ohne den Baum zu verändern: dieselben Elemente, dieselben Props,
  // dieselben `sx`-Werte. Der Vorgabezweig setzt sie in der heutigen Folge
  // zusammen — das ist die „ohne Prop byte-gleich"-Zusicherung (G1b).
  const metaBlock = meta ? (
    <Box sx={{ mt: SP.chip }} {...tid('meta')}>
      {meta}
    </Box>
  ) : null
  const chipsBlock = chips ? (
    <Stack
      direction="row"
      spacing={SP.chip}
      useFlexGap
      flexWrap="wrap"
      sx={{ mt: SP.chip }}
      {...tid('chips')}
    >
      {chips}
    </Stack>
  ) : null

  // 🔴 G4-V — der aufgeklappte Teil, UNTER dem letzten Block, den `slotOrder`
  // setzt (in beiden Reihenfolgen). Ohne die Prop entsteht **kein** Knoten: das
  // ist die additive Zusicherung, und `K.7a` misst sie gegen `SIGNATUR_VORGABE`
  // (ein `git diff` an den Verbrauchern = 0 belegt sie nach Regel 17 §1 NICHT —
  // die Verbraucherdateien bleiben in beiden Faellen unangetastet).
  //
  // ⚠️ Nur im `card`-Zweig: `variant="row"` ist die reduzierte Zeile (N11.7)
  // mit zwei Zeilen, „nichts geht verloren, nichts kommt hinzu" — ein dritter
  // Block machte aus ihr eine dritte Form (Probe K.7d).
  //
  // 🔴 Kein `Collapse`, kein Schalter, kein `aria-expanded` hier: den Zustand
  // haelt der AUFRUFER, wie die Weiche und den Aktionssatz. Ein Baustein, der
  // ihn selbst hielte, haette eine zweite Quelle fuer `expanded`.
  //
  // 🔴 Ein LEERES ARRAY zaehlt wie „nicht gesetzt" (R15 W3, Runde 1). Die
  // natuerliche Aufrufform `expanded={liste.map(…)}` schickt bei leerer Liste
  // `[]` — truthy. Eine reine Truthiness-Wache erzeugte dann einen LEEREN
  // Kasten mit `mt: SP.chip` Abstand; `null`/`false`/`undefined` deckt sie
  // ohnehin schon (K.7a). Probe K.7f.
  const hatExpanded = Array.isArray(expanded) ? expanded.length > 0 : Boolean(expanded)
  const expandedBlock = hatExpanded ? (
    <Box sx={{ mt: SP.chip }}>{expanded}</Box>
  ) : null

  return (
    <Panel
      // 🔴 `none` + `SP.tight` statt `pad="sm"`: die Karte sitzt in einer
      // Liste, nicht als Einzelfläche, und auf 390 px ist der Unterschied
      // zwischen 12 px und 8 px Innenabstand der Platz, um den eine Karte mit
      // einem dreizeiligen Begründungssatz höher wird (gerechnet ~158 px FESTER
      // Teil auf 390 px, Probe `ui2-listen-karten.spec.ts` L.1e — gemessen wird
      // der feste Teil, weil die Länge des Begründungssatzes Datenlage ist).
      // `SP.tight` ist die Tokenstufe darunter — kein Literal.
      pad="none"
      // Kein Schatten: eine Liste aus zwanzig schattierten Flächen liest sich
      // als zwanzig schwebende Karten. Der Rahmen trägt die Trennung, wie in
      // jeder anderen dichten Liste des Hauses.
      shadow="none"
      sx={{ p: SP.tight, ...(sx || {}) }}
      {...rest}
    >
      <Stack direction="row" spacing={SP.sm} alignItems="flex-start">
        {auswahl}
        {leading ? (
          <Box sx={{ flexShrink: 0, display: 'flex' }} {...tid('leading')}>
            {leading}
          </Box>
        ) : null}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack
            direction="row"
            spacing={SP.tight}
            alignItems="center"
            justifyContent="space-between"
          >
            <Box sx={{ minWidth: 0 }} {...tid('title')}>
              {title}
            </Box>
            {actions ? (
              <Box sx={{ flexShrink: 0 }} {...tid('actions')}>
                {actions}
              </Box>
            ) : null}
          </Stack>
          {kennung ? <Box sx={{ mt: SP.chip }}>{kennung}</Box> : null}
          {/* 🔴 N11.2 gegen den gelieferten Baustein: die VORGABE bleibt
              `'meta-first'` — Kerndaten vor Status, die heutige Folge. Wer
              N11.2 braucht (Status vor Kerndaten), setzt `"chips-first"`:
              opt-in, weil die globale Drehung die Faelligkeitskarte um
              rund 28 px nach unten schoebe (L.1g, s. JSDoc). */}
          {slotOrder === 'chips-first' ? (
            <>
              {chipsBlock}
              {metaBlock}
            </>
          ) : (
            <>
              {metaBlock}
              {chipsBlock}
            </>
          )}
          {expandedBlock}
        </Box>
      </Stack>
    </Panel>
  )
}
